import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';

import {
  runtimeActions,
  runtimeSelectors,
  RuntimeState,
  UrlParametersFacade,
} from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import { AccountInfo, ConnectionId, PageInfo } from '@blaze-shared/api-contracts-domain';
import { accountSelectors } from '@blaze-shared/app-state-data-access';
import { ElementEventsUi, ElementOutputUi } from '@blaze-shared/elements-domain';
import { UrlVariable } from '@blaze-shared/projects-domain';
import { BlazeSchema, getSchemaById, WorkflowSchema } from '@blaze-shared/schema-domain';
import { Connection, RuntimeElement, Workflow } from '@blaze-shared/workflow-domain';

import { getElementEntities } from '../elements/elements.selectors';

@Injectable({
  providedIn: 'root',
})
export class RuntimeFacade {
  private readonly pageParametersFacade = inject(UrlParametersFacade);
  private readonly store = inject(Store<RuntimeState>);

  private readonly dryRuns$$ = new Subject<{ elementId: CanvasElementId; state: boolean }>();

  readonly connections$: Observable<Connection[]>;
  readonly dryRuns$ = this.dryRuns$$.asObservable();
  readonly loadedProjectId$: Observable<string>;
  readonly schemas$: Observable<Record<string, BlazeSchema>>;
  readonly workflows$: Observable<Workflow[]>;

  account?: AccountInfo;
  connections: Connection[] = [];
  datasetNames: Map<string, string> = new Map();
  elements: Dictionary<CanvasElement> = {};
  eventsUi: Dictionary<ElementEventsUi> = {};
  initializedUiElements: Dictionary<boolean> = {};
  outputsUi: Dictionary<ElementOutputUi> = {};
  pageId: string | undefined;
  pages: PageInfo[] | undefined;
  projectId: string | undefined;
  schemas: Record<string, BlazeSchema> = {};
  workflowOutputs: Dictionary<any> = {};
  workflows: Workflow[] = [];
  workflowsSchema: Record<string, WorkflowSchema> = {};

  get connectionsValue(): Connection[] {
    return this.connections;
  }
  get elementsValue(): Dictionary<CanvasElement> {
    return this.elements;
  }
  get outputsUiValue(): Dictionary<ElementOutputUi> {
    return this.outputsUi;
  }
  get workflowsSchemaValue(): Record<string, WorkflowSchema> {
    return this.workflowsSchema;
  }
  get workflowsValue(): Workflow[] {
    return this.workflows;
  }

  constructor() {
    this.connections$ = this.store.select(runtimeSelectors.selectConnections);
    this.loadedProjectId$ = this.store.select(runtimeSelectors.selectProjectId);
    this.workflows$ = this.store.select(runtimeSelectors.selectWorkflows);
    this.schemas$ = this.store.select(runtimeSelectors.selectSchemas);

    this.store.select(accountSelectors.selectAccount).subscribe((account) => {
      this.account = account;
    });
    this.store.select(runtimeSelectors.selectConnections).subscribe((connections) => {
      this.connections = connections;
    });
    this.store.select(runtimeSelectors.selectDatasetNames).subscribe((names) => {
      this.datasetNames = new Map(Object.entries(names));
    });
    this.store.select(runtimeSelectors.selectWorkflows).subscribe((workflows) => {
      this.workflows = workflows;
    });
    this.store.select(runtimeSelectors.selectOutputsUi).subscribe((data) => {
      this.outputsUi = data;
    });
    this.store.select(runtimeSelectors.selectEventsUi).subscribe((data) => {
      this.eventsUi = data;
    });
    this.store.select(runtimeSelectors.selectWorkflowOutputs).subscribe((data) => {
      this.workflowOutputs = data;
    });
    this.store.select(runtimeSelectors.selectPageId).subscribe((pageId) => {
      this.pageId = pageId;
    });
    this.store.select(runtimeSelectors.selectPages).subscribe((pages) => {
      this.pages = pages;
    });
    this.store.select(runtimeSelectors.selectProjectId).subscribe((projectId) => {
      this.projectId = projectId;
    });
    this.store.select(runtimeSelectors.selectSchemas).subscribe((schema) => {
      this.schemas = schema;
    });
    this.store.select(runtimeSelectors.selectWorkflowsSchemas).subscribe((schema) => {
      this.workflowsSchema = schema;
    });
    this.store.select(runtimeSelectors.selectInitializedUiElements).subscribe((initInfo) => {
      this.initializedUiElements = initInfo;
    });
    this.store.select(getElementEntities).subscribe((elements) => {
      this.elements = elements;
    });
  }

  getConnectionById(connectionId: string): Connection | null {
    return this.connections.find(({ id }) => id === connectionId) || null;
  }

  getConnectionBySource(sourceElement: RuntimeElement): Connection | null {
    return (
      this.connections.find(
        ({ source }) =>
          source.id === sourceElement.id && source.layerType === sourceElement.layerType,
      ) || null
    );
  }

  getConnectionByTarget(targetElement: RuntimeElement): Connection | null {
    const connection = this.connections.find(
      ({ target }) =>
        target.id === targetElement.id && target.layerType === targetElement.layerType,
    );
    return connection || null;
  }

  /** Resolves a schema by its own id, or - for element schemas - by the element's id. */
  getSchemaById(id: string): BlazeSchema {
    return getSchemaById(id, this.schemas) as BlazeSchema;
  }

  getVarUrl(pageId: string | undefined): UrlVariable[] | undefined {
    return this.pageParametersFacade.getPageVariables(pageId);
  }

  /**
   * Finds and returns specific workflow by its ID.
   */
  getWorkflowById(workflowId: string): Workflow | null {
    return this.workflows.find(({ id }) => id === workflowId) || null;
  }

  getWorkflowInputsToElementsMap(
    workflowId: string | undefined,
  ): Record<CanvasElementId, string> | undefined {
    if (!workflowId) {
      return undefined;
    }

    const inputs = this.getWorkflowById(workflowId)?.inputs;
    if (!inputs) {
      return undefined;
    }

    return Object.entries(inputs).reduce(
      (result, [name, connId]) => {
        const connection = this.getConnectionById(connId);
        if (connection) {
          result[connection.source.id] = name;
        }
        return result;
      },
      {} as Record<CanvasElementId, string>,
    );
  }

  /**
   * Returns all workflows connected to specified target.
   * @param targetEl
   */
  getWorkflowsByTarget(targetEl: RuntimeElement): Workflow[] {
    const connections =
      this.connections.filter(
        ({ target }) => target.id === targetEl.id && target.layerType === targetEl.layerType,
      ) || [];
    const workflows: Workflow[] = [];
    connections.forEach((connection) => {
      const workflow = this.getWorkflowById(connection.source.id);
      if (workflow) {
        workflows.push(workflow);
      }
    });
    return workflows;
  }

  reset(): void {
    this.outputsUi = {};
    this.schemas = {};
    this.workflowOutputs = {};
    this.workflowsSchema = {};
  }

  removeConnection(id: ConnectionId): void {
    // TODO: if needed implement side-effects adjusting the workflows, etc.
    this.store.dispatch(runtimeActions.connectionRemove({ id }));
  }

  updateDryRunStates(elementId: CanvasElementId, state: boolean): void {
    this.dryRuns$$.next({ elementId, state });
  }
}
